import { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Paginate from '../components/Paginate';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Drive from '../components/Drive';
import BackToTop from '../components/BackToTop';

import { useGetDrivesQuery } from '../slices/drivesApiSlice';
import { useListScope, SCOPE_ALL } from '../hooks/useListScope';

// scope='all'  → All Drives (site-wide stats)
// scope='mine' → My Drives (drives the user has claimed benchmarks for,
//                with stats computed from the user's benchmarks only)
export default function DrivesListScreen({ scope = SCOPE_ALL }) {
  const listScope = useListScope(scope);
  const { mine } = listScope;

  // Persist list preferences separately per scope
  const storageKey = (name) => `${mine ? 'myDrives' : 'drives'}${name}`;

  const [sortBy, setSortBy] = useState(
    localStorage.getItem(storageKey('SortBy')) || 'model_asc'
  );
  const [makeFilter, setMakeFilter] = useState(
    localStorage.getItem(storageKey('MakeFilter')) || 'All'
  );
  const [typeFilter, setTypeFilter] = useState(
    localStorage.getItem(storageKey('TypeFilter')) || 'All'
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    Number(localStorage.getItem(storageKey('PerPage'))) || 10
  );
  const [excludeUsb, setExcludeUsb] = useState(
    localStorage.getItem(storageKey('ExcludeUsb')) === 'true'
  );

  const { pageNumber } = useParams();
  const page = Number(pageNumber) || 1;

  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const isLoggedIn = Boolean(userInfo?._id);
  const safeUserId = isLoggedIn ? userInfo._id : undefined;

  const { data, isLoading, error } = useGetDrivesQuery({
    mine,
    userId: safeUserId,
    page,
    pageSize: itemsPerPage,
    sortBy,
    make: makeFilter,
    type: typeFilter,
    excludeUsb,
  });

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Message variant='danger'>
        {error?.data?.message || error.error}
      </Message>
    );
  }

  const drives = data.drives;
  const allMakes = ['All', ...(data.allMakes || [])];
  const allTypes = ['All', ...(data.allTypes || [])];
  const filtersActive = makeFilter !== 'All' || typeFilter !== 'All' || excludeUsb;

  // basePath used to reset to page 1 when filter/pageSize changes
  const basePath = listScope.drivesPath;

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    localStorage.setItem(storageKey('SortBy'), value);
    if (page !== 1) navigate(basePath);
  };

  const handleMakeChange = (e) => {
    const value = e.target.value;
    setMakeFilter(value);
    localStorage.setItem(storageKey('MakeFilter'), value);
    if (page !== 1) navigate(basePath);
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setTypeFilter(value);
    localStorage.setItem(storageKey('TypeFilter'), value);
    if (page !== 1) navigate(basePath);
  };

  const handleItemsPerPageChange = (e) => {
    const value = Number(e.target.value);
    setItemsPerPage(value);
    localStorage.setItem(storageKey('PerPage'), value);
    if (page !== 1) navigate(basePath);
  };

  const handleExcludeUsbChange = (e) => {
    const checked = e.target.checked;
    setExcludeUsb(checked);
    localStorage.setItem(storageKey('ExcludeUsb'), checked);
    if (page !== 1) navigate(basePath);
  };

  return (
    <>
      <h1 style={{ marginTop: '32px' }}>
        {mine ? 'My Drives' : 'All Drives'}{typeof data?.totalDrives === 'number' ? ` (${data.totalDrives})` : ''}
      </h1>
      {mine && (
        <p className='text-muted'>
          Drives from your claimed benchmarks. Stats are averaged from your benchmarks only.
        </p>
      )}

      <div className='d-flex flex-wrap gap-4 mb-4'>
        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Sort Order:</span>
          <select value={sortBy} onChange={handleSortChange} className='form-select form-select--custom w-auto'>
            <optgroup label="Model Name">
              <option className='option--custom' value='model_asc'>Model Name (A → Z)</option>
              <option className='option--custom' value='model_desc'>Model Name (Z → A)</option>
            </optgroup>
            <optgroup label="Make">
              <option className='option--custom' value='make_asc'>Make (A → Z)</option>
              <option className='option--custom' value='make_desc'>Make (Z → A)</option>
            </optgroup>
            <optgroup label="Read Speed">
              <option className='option--custom' value='read_desc'>Read (Highest first)</option>
              <option className='option--custom' value='read_asc'>Read (Lowest first)</option>
            </optgroup>
            <optgroup label="Write Speed">
              <option className='option--custom' value='write_desc'>Write (Highest first)</option>
              <option className='option--custom' value='write_asc'>Write (Lowest first)</option>
            </optgroup>
            <optgroup label="Benchmarks">
              <option className='option--custom' value='numBenchmarks_desc'>Benchmarks (Most first)</option>
              <option className='option--custom' value='numBenchmarks_asc'>Benchmarks (Fewest first)</option>
            </optgroup>
          </select>
        </div>

        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Make:</span>
          <select value={makeFilter} onChange={handleMakeChange} className='form-select form-select--custom w-auto'>
            {allMakes.map(m => (
              <option className='option--custom' key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Type:</span>
          <select value={typeFilter} onChange={handleTypeChange} className='form-select form-select--custom w-auto'>
            {allTypes.map(t => (
              <option className='option--custom' key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Records per Page:</span>
          <select value={itemsPerPage} onChange={handleItemsPerPageChange} className='form-select form-select--custom w-auto'>
            <option className='option--custom' value="5">5</option>
            <option className='option--custom' value="10">10</option>
            <option className='option--custom' value="15">15</option>
            <option className='option--custom' value="20">20</option>
            <option className='option--custom' value="25">25</option>
            <option className='option--custom' value="50">50</option>
          </select>
        </div>

        <div className='d-flex align-items-center'>
          <label className='d-flex align-items-center gap-2 mb-0' style={{ cursor: 'pointer' }}>
            <input
              type='checkbox'
              checked={excludeUsb}
              onChange={handleExcludeUsbChange}
              className='form-check-input mt-0'
            />
            <span className='fw-bold'>Exclude USB</span>
          </label>
        </div>
      </div>

      {drives.length === 0 && (
        <Message variant='info'>
          {filtersActive ? (
            'No drives match the selected filters.'
          ) : mine ? (
            <>
              No drives found. Go to{' '}
              <Link to={`/me/systems`}>My Systems</Link>{' '}
              to claim a system and its benchmarks.
            </>
          ) : (
            'No drives found.'
          )}
        </Message>
      )}

      <Row>
        {drives.map((drive) => (
          <Col key={drive._id} sm={12} md={12} lg={12} xl={12}>
            <Drive drive={drive} to={listScope.drivePath(drive._id)} />
          </Col>
        ))}
      </Row>

      <div className='d-flex justify-content-center' style={{ margin: '32px 0' }}>
        <Paginate
          totalPages={data?.totalPages}
          currentPage={data?.currentPage}
          basePath={basePath}
          firstPageIsBasePath={true}
        />
      </div>
      <BackToTop heroThreshold={200} />
    </>
  );
}
